/**
 * Przewodnik metodyczny — moduł edukacyjny przeniesiony z prototypu GranuFilt.
 *
 * Pokazuje, CO Z CZEGO WYNIKA przy doborze obsypki: znaczenie parametrów, obie ścieżki normowe
 * z tabelami referencyjnymi i tło sufozji. Treść jest statyczna (cytaty z norm i stałe tablice),
 * więc żyje na froncie; część zmienna — kroki wyliczone dla konkretnej próbki — przychodzi
 * z backendu jako `result.guide`.
 *
 * Teksty trzymane są w jednym słowniku `PL`, żeby dołożenie wersji niemieckiej (prototyp ją miał)
 * sprowadzało się do dodania drugiego obiektu i przełącznika, bez ruszania układu.
 */

interface GuideTable {
  caption: string;
  head: string[];
  rows: string[][];
}

interface GuideSection {
  heading: string;
  paragraphs: string[];
  tables?: GuideTable[];
}

const PL: { note: string; sections: GuideSection[] } = {
  note: 'Moduł edukacyjny dla hydrogeologa — pokazuje, co z czego wynika. Nie jest drukowany w raporcie.',
  sections: [
    {
      heading: 'Parametry — co oznaczają i po co je liczymy',
      paragraphs: [
        'd₁₀ — średnica, poniżej której leży 10 % masy próbki. Reprezentuje frakcję drobną; steruje '
          + 'przepuszczalnością (stąd wzory k = f(d₁₀²)) i kryterium stosowania obsypki w PN.',
        'd₅₀ — mediana uziarnienia. W PN-G-02318:1994 jest podstawą doboru obsypki: ziarna obsypki mają '
          + 'być 4–6 razy większe, aby zatrzymać szkielet gruntu, nie dławiąc dopływu.',
        'U = d₆₀/d₁₀ — wskaźnik różnoziarnistości. U ≤ 3: grunt równoziarnisty → potrzebna obsypka (PN) '
          + 'i niski Fg (DVGW). U ≥ 5: grunt różnoziarnisty → Fg = 10 (DVGW), a wg PN możliwy filtr bez obsypki.',
        'Cc = d₃₀²/(d₁₀·d₆₀) — wskaźnik krzywizny; 1–3 świadczy o dobrym uziarnieniu (krzywa bez „schodków").',
        'dg (DVGW W 113) — miarodajna średnica ziarna: środek logarytmiczny najwyższego słupka rozkładu. '
          + 'To najliczniejsza frakcja szkieletu — jej podparcie przez obsypkę zapobiega erozji ściany otworu.',
      ],
    },
    {
      heading: 'Ścieżka polska — PN-G-02318:1994 · PN-88/B-06715 · PN-G-02323:2011',
      paragraphs: [
        'Filtr z obsypką (pkt 2.3.10a) stosuje się w gruntach równoziarnistych: U ≤ 3,0 i d₁₀ ≤ 0,25 mm. '
          + 'Filtr bez obsypki (2.3.10b): U ≥ 3,0 i d₁₀ ≥ 0,025 mm — wypłukanie drobnych frakcji tworzy '
          + 'naturalną obsypkę wokół filtra.',
        'Dobór obsypki (2.3.9): 4 ≤ D/d₅₀ ≤ 6 (metoda obliczeniowa) lub 4,5 ≤ D/dg ≤ 5,0 (metoda graficzna '
          + 'z krzywą wzorcową). Wynikowy przedział D dopasowuje się do frakcji handlowej wg PN-88/B-06715.',
        'Szczelina filtra z obsypką (2.3.11): W ≤ 0,8·Dmin, gdzie Dmin to najmniejsze ziarno wybranej frakcji. '
          + 'Bez obsypki (2.3.12): d₅₀/3 ≤ W ≤ d₅₀; przy U ≥ 6 szczelina ma zatrzymać 60–70 % materiału warstwy, '
          + 'przy U < 6 — 50–60 %; przy wodzie korozyjnej lub niepewnym uziarnieniu +10 %.',
        'Grubość obsypki na stronę (2.3.14, tablica 2) zależy od frakcji przesiewu; maksymalnie 200 mm. '
          + 'W skałach zwięzłych zaleca się uziarnienie 3–8 mm.',
      ],
      tables: [
        {
          caption: 'PN-88/B-06715 — handlowe frakcje piasków i żwirów filtracyjnych (frakcje w nawiasach — za zgodą zamawiającego)',
          head: ['Piasek [mm]', 'Żwir [mm]'],
          rows: [
            ['0,25 – 0,5', '2,0 – 3,15'],
            ['0,5 – 1,0', '3,15 – 5,0  ·  (2,0 – 4,0)'],
            ['0,8 – 1,4', '5,0 – 8,0  ·  8,0 – 16,0'],
            ['1,0 – 2,0', '16,0 – 31,5  ·  (10 – 20) · (20 – 40)'],
          ],
        },
        {
          caption: 'PN-G-02318:1994 tablica 2 — grubość obsypki na stronę',
          head: ['Frakcja przesiewu [mm]', 'min [mm]', 'max [mm]'],
          rows: [['do 2', '50', '200'], ['2 – 8', '60', '200'], ['powyżej 8', '80', '200']],
        },
        {
          caption: 'PN-G-02323:2011 — znormalizowane szczeliny rur filtrowych PVC-U',
          head: ['S₁ [mm]'],
          rows: [['0,5 · 0,75 · 1,0 · 1,5 · 2,0 · 2,5 · 3,0']],
        },
      ],
    },
    {
      heading: 'Ścieżka niemiecka — DVGW W 113 (A) 2024 · DIN 4924 · DVGW W 123 (A)',
      paragraphs: [
        'Ds = dg·Fg. Współczynnik filtru: Fg = 5 + CU dla 1 < CU < 5; Fg = 10 dla CU ≥ 5; przy drganiach '
          + 'lub pracy przerywanej studni Fg = 5. Logika: im bardziej różnoziarnisty grunt, tym większy '
          + 'margines bezpieczeństwa porów obsypki.',
        'Obliczony Ds musi mieścić się wewnątrz wybranej grupy ziarnowej wg DIN 4924; szczelinę filtra '
          + 'przyjmuje się wg tab. 1 W 113 (poniżej).',
        'Minimalna grubość obsypki: 75 mm na stronę (50 mm dla otworów obserwacyjnych; ≥ 100 mm przy '
          + 'materiale > 8 mm). Nadsypka ≥ 2 m lub 10 % długości filtra (DVGW W 123 (A)). Przy Ds powyżej '
          + '16 mm — grunt gruboziarnisty, obsypka zbędna.',
        'Grunt sufozyjny lub bardzo drobny → obsypka dwuwarstwowa: grupy zewnętrzną i wewnętrzną dobiera '
          + 'się parami wg DIN 4924 tab. 2. Aplikacja tego doboru nie automatyzuje — tabela jest podpowiedzią.',
      ],
      tables: [
        {
          caption: 'DVGW W 113 tab. 1 — grupy DIN 4924 i szczeliny filtra',
          head: ['Grupa [mm]', 'Szczelina [mm]'],
          rows: [
            ['0,71 – 1,25', '0,3 – 0,5'], ['1,0 – 1,6', '0,5'], ['1,0 – 2,0', '0,5 – 0,75'],
            ['1,6 – 2,5', '0,75 – 1,0'], ['2,0 – 3,15', '1,0 – 1,5'], ['3,15 – 5,6', '2,0 – 2,5'],
            ['5,6 – 8,0', '3,0 – 4,0'], ['8,0 – 16,0', '4,0 – 5,0'],
          ],
        },
        {
          caption: 'DIN 4924 tab. 2 — pary grup przy obsypce dwuwarstwowej',
          head: ['Zewnętrzna [mm]', 'Wewnętrzna [mm]'],
          rows: [
            ['0,71 – 1,25', '3,15 – 5,6'], ['1,0 – 1,6', '3,15 – 5,6'], ['1,0 – 2,0', '5,6 – 8,0'],
            ['1,6 – 2,5', '5,6 – 8,0'], ['2,0 – 3,15', '8,0 – 16,0'],
          ],
        },
      ],
    },
    {
      heading: 'Współczynnik filtracji k — z krzywej uziarnienia',
      paragraphs: [
        'Beyer (zalecany w W 113): k = C·d₁₀² [m/s; d₁₀ w mm]. C zależy od U (tabela niżej). '
          + 'Zakres stosowania: 0,06 ≤ d₁₀ ≤ 0,6 mm i U ≤ 20.',
        'Hazen: k = 0,0116·d₁₀² (d₁₀ 0,1–3 mm, U < 5). USBSC: k = 0,0036·d₂₀^2,3 (d₂₀ 0,1–3 mm, U < 5). '
          + 'Wyniki empiryczne — traktować jako oszacowanie punktowe; dokładniejsze są próbne pompowania (DVGW W 111).',
        'Metodę aplikacja dobiera automatycznie wg zakresów stosowania: gdy kwalifikuje się USBSC — wygrywa; '
          + 'gdy tylko Beyer i Hazen — Beyer; gdy żadna — Beyer z ostrzeżeniem. Wybór można nadpisać ręcznie.',
        'Przeliczenie: 1 m/s = 86 400 m/d. Przykład: 5·10⁻⁴ m/s = 43,2 m/d.',
      ],
      tables: [
        {
          caption: 'Beyer — współczynnik C w funkcji U (zagęszczenie średnie, 10 °C)',
          head: ['U = d₆₀/d₁₀', 'C'],
          rows: [
            ['1,0 – 1,9', '0,011'], ['2,0 – 2,9', '0,010'], ['3,0 – 4,9', '0,009'],
            ['5,0 – 9,9', '0,008'], ['10,0 – 19,9', '0,007'], ['> 20', '0,006'],
          ],
        },
      ],
    },
    {
      heading: 'Kryteria Terzaghiego — kontrola dobranej obsypki',
      paragraphs: [
        'Szczelność D₁₅/d₈₅ ≤ 4 — obsypka ma zatrzymać szkielet gruntu. Przepuszczalność D₁₅/d₁₅ ≥ 5 — '
          + 'obsypka ma być wyraźnie bardziej przepuszczalna niż warstwa wodonośna, żeby nie dławić dopływu.',
        'Założenie modelowe: obsypka handlowa a–b traktowana jest jako uziarnienie log-równomierne między '
          + 'granicami frakcji, Dₓ = a·(b/a)^(x/100). Producent podaje tylko granice frakcji, nie pełną krzywą, '
          + 'więc kontrola jest orientacyjna — ale wyłapuje dobory jawnie za grube lub za drobne.',
        'Kontroli równoziarnistości obsypki (U ≤ 2,5 wg W 113) aplikacja nie prowadzi: przy powyższym modelu '
          + 'U = √(b/a), a maksimum dla wszystkich frakcji PN-88/B-06715 i grup DIN 4924 wynosi 1,41 — warunek '
          + 'jest spełniony z definicji i kontrola nie wniosłaby informacji.',
      ],
    },
    {
      heading: 'Sufozja — dlaczego to ważne',
      paragraphs: [
        'Jeżeli rozkład uziarnienia ma ≥ 2 wyraźne maksima (grunt „lukowaty"), frakcja drobna nie jest '
          + 'podparta przez szkielet gruboziarnisty i może migrować ku filtrowi — piaszczenie studni, '
          + 'kolmatacja obsypki. DVGW W 113 nakazuje traktować taki grunt ostrożnie.',
        'Praktyka: dla miarodajnej średnicy przyjąć maksimum drobniejsze (bezpieczniej), zastosować obsypkę '
          + 'dwuwarstwową wg DIN 4924 tab. 2 albo zmniejszyć szczelinę. Decyzję dokumentuje geolog '
          + 'w zakładce „Porównanie i decyzja".',
        'Wykrywanie jest heurystyczne: za maksimum uznajemy frakcję o udziale ≥ 8 %, przewyższającą obu '
          + 'sąsiadów o ponad 1,5 punktu procentowego. Progi nie pochodzą z normy — to sygnał do oceny '
          + 'przez geologa, nie rozstrzygnięcie.',
      ],
    },
  ],
};

/** Statyczna część przewodnika: tło metodyczne i tabele referencyjne obu norm. */
export function MethodGuide() {
  return (
    <div className="grid gap-5">
      <div className="text-[11.5px] text-ink-400 italic">{PL.note}</div>
      {PL.sections.map(sec => (
        <section key={sec.heading} className="grid gap-2">
          <h3 className="text-[13.5px] font-bold text-ink-900">{sec.heading}</h3>
          {sec.paragraphs.map((p, i) => (
            <p key={i} className="text-[12.5px] text-ink-700 leading-relaxed">{p}</p>
          ))}
          {sec.tables?.map(t => (
            <div key={t.caption} className="mt-1">
              <div className="text-[11px] font-semibold text-ink-500 mb-1">{t.caption}</div>
              <div className="overflow-x-auto">
                <table className="table-hw [&_th]:!px-2.5 [&_td]:!px-2.5">
                  <thead><tr>{t.head.map(h => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>
                    {t.rows.map((row, ri) => (
                      <tr key={ri} className="!cursor-default hover:!bg-transparent">
                        {row.map((cell, ci) => (
                          <td key={ci} className="font-mono tabular-nums text-ink-700 whitespace-nowrap">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
